/**
 * @module Logger
 * @description Unified logging utility for React/TypeScript frontend following STD_Logging.md standards
 * @stateType local
 * @performance Minimal overhead with conditional logging and batching
 * @accessibility Console output preserved for screen readers in development
 */

import { PALETTES } from '@/theme/tokens';
import { readLogError } from '@/utils/logError';
import type { ApiFailure } from '@/types/api';

// Use crypto.randomUUID() if available, otherwise fallback to timestamp + random
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Logging levels matching backend standards
 * @enum {number}
 */
export enum LogLevel {
  DEBUG = 10,
  INFO = 20,
  WARNING = 30,
  ERROR = 40,
  CRITICAL = 50,
}

/**
 * @interface LogContext
 * @description Request and session tracking information
 * @businessContext Enables correlation of user actions across services
 */
export interface LogContext {
  requestId?: string;
  correlationId?: string;
  userId?: string;
  sessionId?: string;
  projectId?: string;
  agentId?: string;
}

/**
 * @interface LogMetadata
 * @description Code location and version information
 * @businessContext Helps identify source of issues in production
 */
export interface LogMetadata {
  file?: string;
  line?: number;
  function?: string;
  module?: string;
  version?: string;
}

/**
 * @interface LogPerformance
 * @description Performance metrics for optimization
 * @businessContext Identifies performance bottlenecks
 */
export interface LogPerformance {
  durationMs?: number;
  memoryMb?: number;
  cpuPercent?: number;
}

/**
 * @interface LogError
 * @description Structured error information
 * @businessContext Enables automated error analysis and recovery
 */
/**
 * The Chrome-only performance.memory extension, which lib.dom.d.ts does not
 * model. Optional throughout because every other engine omits it.
 */
type PerformanceWithMemory = Performance & {
  memory?: { usedJSHeapSize: number }
}

export interface LogError {
  type: string;
  message: string;
  stackTrace?: string;
  recoveryAction?: string;
  failure?: ApiFailure;
  status?: number;
  requestId?: string;
}

/**
 * @interface AIContext
 * @description AI-relevant patterns for analysis
 * @businessContext Enables pattern recognition and automated suggestions
 */
export interface AIContext {
  pattern?: string;
  tags?: string[];
  severity?: 'low' | 'medium' | 'high' | 'critical';
  suggestedAction?: string;
}

/**
 * @interface LogEntry
 * @description Complete log entry matching unified schema
 * @businessContext Standardized format for cross-platform log analysis
 */
export interface LogEntry {
  timestamp: string;
  level: keyof typeof LogLevel;
  logger: string;
  message: string;
  context?: LogContext;
  metadata?: LogMetadata;
  performance?: LogPerformance;
  error?: LogError;
  aiContext?: AIContext;
  // unknown, not any: custom fields are arbitrary, so consumers should narrow
  // before use rather than inherit an escape hatch on every LogEntry read.
  [key: string]: unknown;
}

/**
 * @interface LoggerConfig
 * @description Logger configuration options
 * @businessContext Allows runtime configuration of logging behavior
 */
export interface LoggerConfig {
  logLevel?: LogLevel;
  isEnabled?: boolean;
  enableBatching?: boolean;
  batchSize?: number;
  batchTimeout?: number;
  enableAIContext?: boolean;
  sendToBackend?: boolean;
  redactedFields?: string[];
}

/**
 * @class Logger
 * @description Singleton logger implementation with batching and redaction
 * @stateType singleton
 * @performance Batches logs to reduce network overhead
 */
class Logger {
  private static instances: Map<string, Logger> = new Map();
  private logLevel: LogLevel = LogLevel.INFO;
  private isEnabled: boolean = true;
  private enableBatching: boolean = true;
  private batchSize: number = 10;
  private batchTimeout: number = 5000; // 5 seconds
  private enableAIContext: boolean = true;
  private enableBackendLogging: boolean = true;
  private context: LogContext = {};
  private loggerName: string;
  private logQueue: LogEntry[] = [];
  private batchTimer: NodeJS.Timeout | null = null;

  private redactedFields = new Set([
    'password', 'token', 'apiKey', 'secret', 'authorization',
    'creditCard', 'ssn', 'email', 'ipAddress', 'phone',
    'jwt', 'bearer', 'credentials', 'privateKey'
  ]);

  private errorPatterns = new Map<string, AIContext>([
    ['network', {
      pattern: 'network_error',
      tags: ['network', 'connectivity'],
      severity: 'high',
      suggestedAction: 'Check network connectivity and retry'
    }],
    ['timeout', {
      pattern: 'timeout_error',
      tags: ['performance', 'timeout'],
      severity: 'high',
      suggestedAction: 'Increase timeout or optimize operation'
    }],
    ['validation', {
      pattern: 'validation_error',
      tags: ['input', 'validation'],
      severity: 'low',
      suggestedAction: 'Check input data format'
    }],
    ['auth', {
      pattern: 'authentication_error',
      tags: ['security', 'auth'],
      severity: 'medium',
      suggestedAction: 'Verify credentials and permissions'
    }],
    ['permission', {
      pattern: 'authorization_error',
      tags: ['security', 'permissions'],
      severity: 'medium',
      suggestedAction: 'Check user permissions'
    }]
  ]);

  private constructor(loggerName: string, config?: LoggerConfig) {
    this.loggerName = loggerName;
    this.loadConfiguration(config);
  }

  /**
   * Get or create a logger instance
   * @param {string} loggerName - Name for the logger (e.g., module name)
   * @param {LoggerConfig} config - Optional configuration
   * @returns {Logger} Logger instance
   * @performance Reuses existing instances to minimize memory
   */
  static getInstance(loggerName: string = 'app', config?: LoggerConfig): Logger {
    if (!Logger.instances.has(loggerName)) {
      Logger.instances.set(loggerName, new Logger(loggerName, config));
    }
    return Logger.instances.get(loggerName)!;
  }

  /**
   * Load configuration from environment and options
   * @param {LoggerConfig} config - Configuration options
   * @businessContext Allows runtime configuration changes
   */
  private loadConfiguration(config?: LoggerConfig): void {
    // Load from environment
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL || 'INFO';
    this.logLevel = LogLevel[envLogLevel as keyof typeof LogLevel] || LogLevel.INFO;
    this.isEnabled = import.meta.env.VITE_LOGGING_ENABLED !== 'false';
    this.enableBackendLogging = import.meta.env.VITE_LOG_TO_BACKEND !== 'false';

    // Apply provided configuration
    if (config) {
      if (config.logLevel !== undefined) this.logLevel = config.logLevel;
      if (config.isEnabled !== undefined) this.isEnabled = config.isEnabled;
      if (config.enableBatching !== undefined) this.enableBatching = config.enableBatching;
      if (config.batchSize !== undefined) this.batchSize = config.batchSize;
      if (config.batchTimeout !== undefined) this.batchTimeout = config.batchTimeout;
      if (config.enableAIContext !== undefined) this.enableAIContext = config.enableAIContext;
      if (config.sendToBackend !== undefined) this.enableBackendLogging = config.sendToBackend;
      if (config.redactedFields) {
        config.redactedFields.forEach(field => this.redactedFields.add(field));
      }
    }
  }

  /**
   * Set context for all future log entries
   * @param {Partial<LogContext>} context - Context to merge
   * @businessContext Tracks user sessions and correlates actions
   */
  setContext(context: Partial<LogContext>): void {
    this.context = { ...this.context, ...context };
  }

  /**
   * Clear specific context fields
   * @param {string[]} fields - Fields to clear
   */
  clearContext(...fields: (keyof LogContext)[]): void {
    fields.forEach(field => delete this.context[field]);
  }

  /**
   * Check if a log level should be output
   * @param {LogLevel} level - Level to check
   * @returns {boolean} Whether to log
   * @performance Early return prevents unnecessary processing
   */
  private shouldLog(level: LogLevel): boolean {
    return this.isEnabled && level >= this.logLevel;
  }

  /**
   * Redact sensitive information from data
   * @param {unknown} data - Data to redact
   * @returns {unknown} Redacted data
   * @businessContext Prevents sensitive data exposure
   */
  // Generic so callers keep their type: redaction replaces values, never the
  // shape. The one cast that costs is confined to the return below rather than
  // leaking `unknown` (or worse, `any`) out to every call site.
  private redactSensitive<T>(data: T): T {
    if (data === null || data === undefined) return data;
    if (typeof data !== 'object') return data;

    if (Array.isArray(data)) {
      // Same assertion as the object branch: the mapped array is element-wise the
      // same shape, but TypeScript cannot narrow T to an array type from the guard.
      return data.map(item => this.redactSensitive(item)) as T;
    }

    const redacted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      if (Array.from(this.redactedFields).some(field => lowerKey.includes(field))) {
        redacted[key] = '***REDACTED***';
      } else if (typeof value === 'object') {
        redacted[key] = this.redactSensitive(value);
      } else {
        redacted[key] = value;
      }
    }
    return redacted as T;
  }

  /**
   * Extract stack trace information
   * @returns {LogMetadata} Metadata with file/line information
   * @performance Only runs in development mode
   */
  private extractStackInfo(): LogMetadata {
    const metadata: LogMetadata = {};

    if (import.meta.env.DEV && typeof Error.captureStackTrace === 'function') {
      const stack = new Error().stack;
      if (stack) {
        // Extract file, line, and function from stack trace
        const lines = stack.split('\n');
        // Skip first 3 lines (Error, extractStackInfo, createLogEntry)
        const relevantLine = lines[4];
        if (relevantLine) {
          const match = relevantLine.match(/at\s+(\S+)?\s*\(([^:]+):(\d+):(\d+)\)/);
          if (match) {
            metadata.function = match[1] || 'anonymous';
            metadata.file = match[2];
            metadata.line = parseInt(match[3], 10);
          }
        }
      }
    }

    metadata.version = import.meta.env.VITE_APP_VERSION || '1.0.0';
    return metadata;
  }

  /**
   * Identify AI context for errors
   * @param {string} message - Error message
   * @param {{ message?: string }} error - Error details used for classification
   * @returns {AIContext} AI analysis context
   * @businessContext Enables automated error categorization
   */
  // Takes the shape it actually uses rather than Error: callers pass a LogError,
  // a different interface that merely happens to carry a message. The previous
  // `as any` at the call site hid that mismatch.
  private identifyAIContext(message: string, error?: { message?: string }): AIContext | undefined {
    if (!this.enableAIContext) return undefined;

    const combinedText = `${message} ${error?.message || ''}`.toLowerCase();

    for (const [keyword, context] of this.errorPatterns) {
      if (combinedText.includes(keyword)) {
        return context;
      }
    }

    // Default context for unrecognized errors
    return {
      pattern: 'unknown_error',
      tags: ['unclassified'],
      severity: 'medium',
      suggestedAction: 'Review error details and logs'
    };
  }

  /**
   * Create a structured log entry
   * @param {keyof typeof LogLevel} level - Log level
   * @param {string} message - Log message
   * @param {Partial<LogEntry>} extra - Additional fields
   * @returns {LogEntry} Complete log entry
   */
  private createLogEntry(
    level: keyof typeof LogLevel,
    message: string,
    extra?: Partial<LogEntry>
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      logger: this.loggerName,
      message,
      context: {
        ...this.context,
        requestId: this.context.requestId || generateUUID()
      },
      metadata: this.extractStackInfo(),
      ...extra
    };

    // Add AI context for errors
    if ((level === 'ERROR' || level === 'CRITICAL') && this.enableAIContext) {
      const classified = this.identifyAIContext(message, extra?.error)
      const caller = extra?.aiContext
      // Explicit caller values win; omitted values retain classifier defaults.
      entry.aiContext = {
        pattern: caller?.pattern ?? classified?.pattern,
        severity: caller?.severity ?? classified?.severity,
        suggestedAction: caller?.suggestedAction ?? classified?.suggestedAction,
        tags: [...new Set([...(classified?.tags ?? []), ...(caller?.tags ?? [])])],
      }
    }

    return this.redactSensitive(entry);
  }

  /**
   * Output log entry to console and/or backend
   * @param {LogEntry} entry - Log entry to output
   * @performance Batches backend calls to reduce network overhead
   */
  private async output(entry: LogEntry): Promise<void> {
    // Console output in development
    if (import.meta.env.DEV || !this.enableBackendLogging) {
      const consoleMethod = this.getConsoleMethod(entry.level);
      const style = this.getConsoleStyle(entry.level);

      const consoleFunc = console[consoleMethod] as (...args: unknown[]) => void;
      consoleFunc(
        `%c[${entry.level}]%c ${entry.message}`,
        style,
        'color: inherit',
        entry
      );
    }

    // Send to backend in production
    if (this.enableBackendLogging && import.meta.env.PROD) {
      if (this.enableBatching) {
        this.addToBatch(entry);
      } else {
        await this.sendToBackend(entry);
      }
    }
  }

  /**
   * Get appropriate console method for log level
   * @param {keyof typeof LogLevel} level - Log level
   * @returns {keyof Console} Console method name
   */
  private getConsoleMethod(level: keyof typeof LogLevel): keyof Console {
    switch (level) {
      case 'DEBUG': return 'debug';
      case 'INFO': return 'info';
      case 'WARNING': return 'warn';
      case 'ERROR':
      case 'CRITICAL': return 'error';
      default: return 'log';
    }
  }

  /**
   * Get console styling for log level
   *
   * Colours come from the shared token palette rather than literal hex, so
   * DevTools output stays the same family of colour as the rendered app.
   * Console styling carries no contrast contract of its own — it is a
   * developer-facing surface, not rendered UI — so it borrows values rather
   * than being pinned by the token contrast guard.
   *
   * @param {keyof typeof LogLevel} level - Log level
   * @returns {string} CSS style string
   */
  private getConsoleStyle(level: keyof typeof LogLevel): string {
    const palette = PALETTES[typeof document !== 'undefined' && document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'];
    switch (level) {
      case 'DEBUG': return `color: ${palette['text-faint']}; font-weight: normal`;
      case 'INFO': return `color: ${palette.info}; font-weight: bold`;
      case 'WARNING': return `color: ${palette.warning}; font-weight: bold`;
      case 'ERROR': return `color: ${palette.error}; font-weight: bold`;
      case 'CRITICAL': return `color: ${palette.error}; font-weight: bold; background: ${palette.canvas}`;
      default: return 'color: inherit';
    }
  }

  /**
   * Add log entry to batch queue
   * @param {LogEntry} entry - Log entry to batch
   * @performance Reduces API calls through batching
   */
  private addToBatch(entry: LogEntry): void {
    this.logQueue.push(entry);

    if (this.logQueue.length >= this.batchSize) {
      this.flushBatch();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.flushBatch(), this.batchTimeout);
    }
  }

  /**
   * Flush batched logs to backend
   * @performance Single API call for multiple logs
   */
  private async flushBatch(): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.logQueue.length === 0) return;

    const batch = [...this.logQueue];
    this.logQueue = [];

    try {
      await fetch('/api/v1/logs/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs: batch })
      });
    } catch (error) {
      // Fallback to console if backend fails
      console.error('Failed to send log batch:', error, batch);
    }
  }

  /**
   * Send single log entry to backend
   * @param {LogEntry} entry - Log entry to send
   */
  private async sendToBackend(entry: LogEntry): Promise<void> {
    try {
      await fetch('/api/v1/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      });
    } catch (error) {
      console.error('Failed to send log entry:', error, entry);
    }
  }

  // Public logging methods

  /**
   * Log debug message
   * @param {string} message - Debug message
   * @param {Partial<LogEntry>} extra - Additional fields
   */
  debug(message: string, extra?: Partial<LogEntry>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.output(this.createLogEntry('DEBUG', message, extra));
    }
  }

  /**
   * Log info message
   * @param {string} message - Info message
   * @param {Partial<LogEntry>} extra - Additional fields
   */
  info(message: string, extra?: Partial<LogEntry>): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.output(this.createLogEntry('INFO', message, extra));
    }
  }

  /**
   * Log warning message
   * @param {string} message - Warning message
   * @param {Partial<LogEntry>} extra - Additional fields
   */
  warning(message: string, extra?: Partial<LogEntry>): void {
    if (this.shouldLog(LogLevel.WARNING)) {
      this.output(this.createLogEntry('WARNING', message, extra));
    }
  }

  /**
   * Log error message
   * @param {string} message - Error message
   * @param {unknown} error - Original thrown value
   * @param {Partial<LogEntry>} extra - Additional fields
   */
  error(message: string, error?: unknown, extra?: Partial<LogEntry>): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const logEntry = this.createLogEntry('ERROR', message, {
        ...extra,
        error: readLogError(error, extra?.error)
      });
      this.output(logEntry);
    }
  }

  /**
   * Log critical error
   * @param {string} message - Critical error message
   * @param {Error} error - Error object
   * @param {Partial<LogEntry>} extra - Additional fields
   */
  critical(message: string, error?: Error, extra?: Partial<LogEntry>): void {
    if (this.shouldLog(LogLevel.CRITICAL)) {
      const logEntry = this.createLogEntry('CRITICAL', message, {
        ...extra,
        error: error ? {
          type: error.name,
          message: error.message,
          stackTrace: error.stack, // Always include stack for critical
          recoveryAction: extra?.error?.recoveryAction || 'Immediate intervention required'
        } : extra?.error,
        aiContext: {
          severity: 'critical',
          suggestedAction: 'Alert on-call engineer immediately',
          ...(extra?.aiContext || {})
        }
      });
      this.output(logEntry);

      // Immediately flush critical logs
      if (this.enableBatching && this.logQueue.length > 0) {
        this.flushBatch();
      }
    }
  }

  /**
   * Measure and log performance of an operation
   * @param {string} operation - Operation name
   * @param {() => T | Promise<T>} fn - Function to measure
   * @returns {T | Promise<T>} Function result
   * @performance Automatically captures timing metrics
   */
  async measurePerformance<T>(
    operation: string,
    fn: () => T | Promise<T>
  ): Promise<T> {
    const startTime = performance.now();
    const startMemory = (performance as PerformanceWithMemory).memory?.usedJSHeapSize;

    try {
      const result = await Promise.resolve(fn());

      const durationMs = performance.now() - startTime;
      const endMemory = (performance as PerformanceWithMemory).memory?.usedJSHeapSize;
      const memoryMb = startMemory && endMemory
        ? (endMemory - startMemory) / (1024 * 1024)
        : undefined;

      this.info(`Operation completed: ${operation}`, {
        performance: {
          durationMs,
          memoryMb
        }
      });

      return result;
    } catch (error) {
      const durationMs = performance.now() - startTime;

      this.error(`Operation failed: ${operation}`, error as Error, {
        performance: { durationMs }
      });

      throw error;
    }
  }

  /**
   * Flush any pending logs
   * @businessContext Ensures logs are sent before page unload
   */
  async flush(): Promise<void> {
    if (this.enableBatching && this.logQueue.length > 0) {
      await this.flushBatch();
    }
  }
}

// Export singleton instance with default configuration
export const logger = Logger.getInstance();

// Export Logger class for custom instances
export { Logger };

// Window error handler. React's own failures are reported by the root callbacks
// in utils/rootErrorReporting.ts, which replace React's reportError path — these
// listeners only ever see errors React never observed.
if (typeof window !== 'undefined') {
  // Flush logs on page unload
  window.addEventListener('beforeunload', () => {
    logger.flush();
  });

  // Log unhandled errors
  window.addEventListener('error', (event) => {
    logger.critical('Unhandled error', event.error, {
      metadata: {
        file: event.filename,
        line: event.lineno,
        module: 'window.onerror'
      }
    });
  });

  // Log unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', event.reason, {
      error: {
        type: 'UnhandledPromiseRejection',
        message: String(event.reason),
        recoveryAction: 'Add error handling to async operations'
      }
    });
  });
}
